import React, { CSSProperties, ReactNode } from 'react';
import { Action as TinyFabAction, Fab as TinyFab } from './TinyFab';
import classnames from 'classnames';

/**
 * {@docCategory Fab}
 */
export interface FabProps {
   /**
    * The icon to display.
    */
   icon: ReactNode;

   /**
    * Called when the Fab is clicked. Do NOT set this if you have actions set.
    */
   onClick?: () => void;

   /**
    * Array of actions.
    */
   actions?: Array<FabActionProps>;

   /**
    * Number from 1 to 8 for setting color.
    * Internally we mod the number by 8 to get an actual color index.
    */
   color?: number;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Extra bottom offset.  If in a TabBarPanel component, set this to 'tabbar'.
    */
   bottomOffset?: BottomOffsetType;
}

/**
 * {@docCategory Fab}
 */
export interface FabActionProps {
   /**
    * The icon to display.
    */
   icon: ReactNode;

   /**
    * Called when the action is clicked.
    */
   onClick?: () => void;

   /**
    * Number from 1 to 8 for setting color.
    * Internally we mod the number by 8 to get an actual color index.
    */
   color?: number;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;
}

/**
 * {@docCategory Fab}
 */
export declare type BottomOffsetType = number | 'tabbar';

const baseBottomOffset = 32;

const bottomOffsets = {
   'tabbar': 48
};

const getBottomOffset = (bottomOffset?: BottomOffsetType): number => {
   if (bottomOffset === undefined || bottomOffset === null) {
      return 0;
   }

   if (typeof bottomOffset === 'number') {
      return bottomOffset;
   }

   return bottomOffsets[bottomOffset];
};

const getColorClassName = (color?: number) => {
   if (color !== undefined) {
      return `uf-fab-color-${color}`;
   }

   return undefined;
};

export const Fab = (props: FabProps) => {
   const className = classnames('uf-fab', props.className, getColorClassName(props.color));
   const bottomOffset = getBottomOffset(props.bottomOffset);
   const style = Object.assign(props.style || {},
                               { bottom: `${baseBottomOffset + bottomOffset}px` });

   return (
      <TinyFab
         icon={props.icon}
         style={style}
         className={className}
         onClick={props.onClick}
      >
         {mapActions(props)}
      </TinyFab>
   );
};

const mapActions = (props: FabProps) => {
   if (props.actions) {
      return props.actions.map((action, index) => {
         const className = classnames('uf-fab-action', getColorClassName(action.color), action.className);

         return (
            <TinyFabAction
               key={index}
               onClick={action.onClick}
               style={action.style}
               className={className}
            >
               {action.icon}
            </TinyFabAction>
         );
      });
   }

   return undefined;
};
